import React, {useEffect} from 'react';
import {Button, SectionNotification} from '@wix/wix-ui-tpa/cssVars';
import {Error as ErrorIcon} from '@wix/wix-ui-icons-common/on-stage';
import {useLocaleKeys} from '../../../../../../locale-keys/LocaleKeys';
import {useControllerProps} from '../../../../Widget/ControllerContext';
import {getCountryByKey} from '@wix/locale-dataset-javascript';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {CheckoutLoadedWithErrorReason} from '../../../../../../domain/utils/bi.util';
import {classes} from './NoOnlinePaymentsNotification.st.css';

export enum NoOnlinePaymentsNotificationDataHooks {
  Root = 'NoOnlinePaymentsNotification.Root',
  Icon = 'NoOnlinePaymentsNotification.Icon',
  Title = 'NoOnlinePaymentsNotification.Title',
  Body = 'NoOnlinePaymentsNotification.Body',
  CtaButton = 'NoOnlinePaymentsNotification.CtaButton',
}

export const NoOnlinePaymentsNotification = ({billingCountry}: {billingCountry?: string}) => {
  const localeKeys = useLocaleKeys();
  const {t} = useTranslation();
  const {
    checkoutStore: {reportPaymentStepLoadedWithError, reportCheckoutErrorCtaClicked},
    navigationStore: {isOwnerOrPreview, paymentMethodsUrl},
  } = useControllerProps();

  useEffect(() => {
    reportPaymentStepLoadedWithError();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countryDisplayName = billingCountry ? getCountryByKey(billingCountry)?.displayName : undefined;
  const location = countryDisplayName ? t(countryDisplayName) : '';

  const title = isOwnerOrPreview
    ? localeKeys.checkout.noOnlinePayments.merchantError.title({location})
    : localeKeys.checkout.noOnlinePayments.error.title({location});

  const body = isOwnerOrPreview
    ? localeKeys.checkout.noOnlinePayments.merchantError.body()
    : localeKeys.checkout.noOnlinePayments.error.body();

  const ctaText = localeKeys.checkout.noOnlinePayments.merchantError.cta();

  const onButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    reportCheckoutErrorCtaClicked(CheckoutLoadedWithErrorReason.NoPaymentProviderError, ctaText);
    window.open(paymentMethodsUrl, '_blank');
  };

  return (
    <SectionNotification data-hook={NoOnlinePaymentsNotificationDataHooks.Root} type="error">
      <SectionNotification.Text>
        <div className={classes.content}>
          <div className={classes.textAndIcon}>
            <ErrorIcon data-hook={NoOnlinePaymentsNotificationDataHooks.Icon} size={32} />
            <span data-hook={NoOnlinePaymentsNotificationDataHooks.Title} className={classes.title}>
              {title}
            </span>
            <span data-hook={NoOnlinePaymentsNotificationDataHooks.Body}>{body}</span>
          </div>
          {isOwnerOrPreview && (
            <div className={classes.ctaContainer}>
              <Button
                className={classes.ctaButton}
                upgrade
                as="a"
                data-hook={NoOnlinePaymentsNotificationDataHooks.CtaButton}
                href={paymentMethodsUrl}
                onClick={onButtonClick}>
                {ctaText}
              </Button>
            </div>
          )}
        </div>
      </SectionNotification.Text>
    </SectionNotification>
  );
};
